/**
 * A FRISSÍTÉS GOMB ÁLLAPOTA — tiszta függvények, hogy mérhetők legyenek (#54).
 *
 * A szerver mondja meg, hol tart a kérés (`GET /tensoft/refresh-status`); ez a
 * modul dönti el, mit lát belőle a könyvelő. Az artboard:
 * `docs/tervezes/design/tensoft-frissites/tensoft-frissites.html`.
 */

import { formatDate, formatDateTime } from '@/i18n/generated';

/** A backend `TensoftRefreshState`-je. */
export type TensoftRefreshState =
  | 'WAITING'
  | 'PICKED_UP'
  | 'PROCESSING'
  | 'DONE'
  | 'FAILED'
  | 'EXPIRED';

export interface TensoftRefreshView {
  id: string;
  state: TensoftRefreshState;
  requestedAt: string;
  requestedBy: string | null;
  /** A néző kérte-e — a szerver mondja, nem név-összevetés. */
  mine: boolean;
  pickedUpAt: string | null;
  fulfilledAt: string | null;
  expiresAt: string;
  failureReason: string | null;
}

export interface TensoftAgentPresence {
  label: string;
  lastSeenAt: string | null;
  /** A szerver órája szerint 3 percnél régebben jelentkezett. */
  silent: boolean;
}

export interface TensoftRefreshStatus {
  /** Az érvényes adat állapotának ideje — a fejléc „Tensoft HH:MM"-je. */
  dataAsOf: string | null;
  request: TensoftRefreshView | null;
  /** `null`: nincs bekötött feltöltő gép. */
  agent: TensoftAgentPresence | null;
}

/**
 * 24 ÓRA UTÁN RÉGI AZ ADAT (Viktor, 10-05). A napi kör munkanaponként egyszer
 * fut (`tensoft-osszekottetes.md` §3.3/j) — egy kimaradt kör után a
 * következő munkanap reggelén már jelez.
 */
export const OLD_AFTER_MS = 24 * 60 * 60 * 1000;

/** A gomb ezekben nem nyomható: a kérés teljesítés alatt áll (szerződés §3). */
export function inFlight(request: TensoftRefreshView | null): boolean {
  return (
    request !== null &&
    (request.state === 'WAITING' || request.state === 'PICKED_UP' || request.state === 'PROCESSING')
  );
}

export function isOld(dataAsOf: string | null, now: number): boolean {
  return dataAsOf !== null && now - new Date(dataAsOf).getTime() > OLD_AFTER_MS;
}

/**
 * A FEJLÉC IDEJE: ma `15:50`, máskor `10-03 15:50`. A dátum csak akkor kerül
 * elé, ha nem a mai — a napi munkában a perc a hír, a dátum zaj; a régi adatnál
 * viszont pont a dátum a hír, és ott el sem maradhat.
 */
export function stampTime(value: string, now: number): string {
  const full = formatDateTime(value);
  return formatDate(value) === formatDate(new Date(now)) ? full.slice(11) : full.slice(5);
}

/**
 * MI KERÜL A SÁVBA, amíg nincs megnyugtatott kimenet (`Outcome`). A sorrend
 * számít: a teljesítés alatt álló kérés mond többet, mint a régi adat — a
 * könyvelő épp a régi adat miatt nyomta meg a gombot.
 */
export type LiveNotice =
  | { kind: 'noAgent' }
  | { kind: 'silent'; label: string; lastSeenAt: string | null }
  | { kind: 'waiting'; label: string }
  | { kind: 'working'; pickedUpAt: string }
  | { kind: 'colleague'; name: string | null; requestedAt: string }
  | { kind: 'old'; dataAsOf: string }
  | null;

export function liveNotice(status: TensoftRefreshStatus | null, now: number): LiveNotice {
  if (status === null) return null;
  if (status.request !== null && inFlight(status.request)) {
    return requestNotice(status.request, status.agent);
  }
  if (status.dataAsOf !== null && isOld(status.dataAsOf, now)) {
    return { kind: 'old', dataAsOf: status.dataAsOf };
  }
  return null;
}

/** A teljesítés alatt álló kérés jelzése. */
function requestNotice(
  request: TensoftRefreshView,
  agent: TensoftAgentPresence | null
): Exclude<LiveNotice, null> {
  // A HALLGATÓ GÉP ELŐBB, mint a „kolléga kérte": a kérést így sem viszi el
  // senki, és ezt kell tudni azonnal — nem 10 perc múlva (Viktor, 10-05). Az
  // átvett kérésnél nem: ott a gép bizonyosan él.
  const waiting = request.state === 'WAITING';
  if (waiting && agent === null) return { kind: 'noAgent' };
  if (waiting && agent?.silent) {
    return { kind: 'silent', label: agent.label, lastSeenAt: agent.lastSeenAt };
  }
  if (!request.mine) {
    return { kind: 'colleague', name: request.requestedBy, requestedAt: request.requestedAt };
  }
  if (waiting) return { kind: 'waiting', label: agent?.label ?? '' };
  return { kind: 'working', pickedUpAt: request.pickedUpAt ?? request.requestedAt };
}

/**
 * EGY LEZÁRULT KÉRÉS KIMENETE — csak akkor, ha a felület LÁTTA menet közben.
 * Egy tegnap lejárt kérés a betöltéskor nem hír.
 */
export type Outcome =
  | { kind: 'arrived'; dataAsOf: string | null }
  | { kind: 'expired'; dataAsOf: string | null }
  | { kind: 'failed'; reason: string | null; dataAsOf: string | null }
  | { kind: 'sendFailed' }
  | { kind: 'forbidden' };

export interface FollowMemory {
  /** A menet közben látott kérés — ennek a lezárulása hír. */
  watching: string | null;
  /**
   * A figyelt kérés legutóbb látott állapota. A LEJÁRT KÉRÉST TOVÁBB FIGYELJÜK:
   * a szerver a későn érkező pillanatképpel is teljesíti (szerződés §3), és
   * akkor a „lejárt" helyét a „megérkezett" veszi át (review, 10-05).
   */
  seen: TensoftRefreshState | null;
  /** Az előző válasz adat-ideje. `undefined`: még nem jött válasz. */
  dataAsOf: string | null | undefined;
}

export const EMPTY_FOLLOW: FollowMemory = { watching: null, seen: null, dataAsOf: undefined };

export interface FollowStep {
  memory: FollowMemory;
  /** Új kimenet a sávba, ha van. */
  outcome: Outcome | null;
  /** A lista frissüljön — megérkezett az adat (a gombtól vagy a napi körből). */
  reload: boolean;
}

/**
 * EGY ÚJ VÁLASZ FELDOLGOZÁSA.
 *
 * A lista két okból frissül: a figyelt kérés kész lett, VAGY az adat ideje
 * előrelépett (napi kör, egy kolléga kérése). Az első válasz soha nem
 * frissít: az a betöltés, nem változás.
 */
export function followStep(memory: FollowMemory, next: TensoftRefreshStatus): FollowStep {
  const { request } = next;
  let { watching, seen } = memory;
  let outcome: Outcome | null = null;

  if (request !== null && inFlight(request)) {
    watching = request.id;
    seen = request.state;
  } else if (request !== null && watching === request.id && seen !== request.state) {
    outcome = outcomeOf(request, next.dataAsOf);
    seen = request.state;
    // A lejárt kérés még teljesülhet — azt tovább figyeljük; a többi végleges.
    if (request.state !== 'EXPIRED') watching = null;
  }

  const advanced =
    memory.dataAsOf !== undefined &&
    next.dataAsOf !== null &&
    (memory.dataAsOf === null ||
      new Date(next.dataAsOf).getTime() > new Date(memory.dataAsOf).getTime());

  return {
    memory: { watching, seen, dataAsOf: next.dataAsOf },
    outcome,
    reload: advanced || outcome?.kind === 'arrived',
  };
}

/** Egy lezárult kérés kimenete. */
function outcomeOf(request: TensoftRefreshView, dataAsOf: string | null): Outcome | null {
  switch (request.state) {
    case 'DONE':
      return { kind: 'arrived', dataAsOf };
    case 'EXPIRED':
      return { kind: 'expired', dataAsOf };
    case 'FAILED':
      return { kind: 'failed', reason: request.failureReason, dataAsOf };
    default:
      return null;
  }
}
